/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { operationsApi } from "./api";
import useModalA11y from "../utils/useModalA11y";
import "./StaffDialogs.css";

const SHIFTS = [
  { value: "MORNING", label: "Morning (6 AM – 2 PM)" },
  { value: "AFTERNOON", label: "Afternoon (2 PM – 10 PM)" },
  { value: "NIGHT", label: "Night (10 PM – 6 AM)" },
  { value: "FULL_DAY", label: "Full Day" },
];

export default function DutyRosterForm({ onClose, onSaved }) {
  const modalRef = useModalA11y({ isOpen: true, onClose });
  const todayISO = new Date().toISOString().slice(0, 10);
  const [onDate, setOnDate] = useState(todayISO);
  const [allStaff, setAllStaff] = useState([]);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadStaffAndDay = useCallback(async () => {
    const s = await operationsApi.listStaff();
    const flat = [];
    if (s.facility_manager) flat.push(s.facility_manager);
    s.sections.forEach((sec) => flat.push(...sec.members));
    setAllStaff(flat);
    try {
      const existing = await operationsApi.dutyForDate(onDate);
      if (existing && existing.length > 0) {
        setEntries(
          existing.map((r) => ({
            staff_id: r.staff_id,
            shift: r.shift,
          }))
        );
      } else {
        // Seed with one empty row so the form isn't blank.
        setEntries([{ staff_id: "", shift: "MORNING" }]);
      }
    } catch (_) {
      setEntries([{ staff_id: "", shift: "MORNING" }]);
    }
  }, [onDate]);

  useEffect(() => {
    loadStaffAndDay().catch(() => {});
  }, [loadStaffAndDay]);

  const update = (idx, k, v) =>
    setEntries((p) => p.map((e, i) => (i === idx ? { ...e, [k]: v } : e)));
  const addRow = () =>
    setEntries((p) => [...p, { staff_id: "", shift: "MORNING" }]);
  const removeRow = (idx) =>
    setEntries((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault();
    const cleaned = entries.filter((row) => row.staff_id);
    setBusy(true);
    setError("");
    try {
      await operationsApi.replaceDuty({
        on_date: onDate,
        entries: cleaned.map((row) => ({
          staff_id: row.staff_id,
          shift: row.shift,
        })),
      });
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vsop-overlay" onClick={onClose}>
      <form
        ref={modalRef}
        className="vsop-modal vsop-modal-wide"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vsop-roster-title"
      >
        <div className="vsop-modal-head">
          <h2 id="vsop-roster-title">Today's duty roster</h2>
          <button
            type="button"
            className="vsop-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <p className="vsop-modal-helper">
          Pick who's on which shift today. Residents see the on-call
          widget on the home page based on this list.
        </p>

        <div className="vsop-modal-field">
          <label htmlFor="vsop-roster-date">Date</label>
          <input
            id="vsop-roster-date"
            type="date"
            value={onDate}
            onChange={(e) => setOnDate(e.target.value)}
          />
        </div>

        <div className="vsop-roster-rows">
          {entries.map((row, idx) => (
            <div key={idx} className="vsop-roster-row">
              <select
                value={row.staff_id}
                onChange={(e) => update(idx, "staff_id", e.target.value)}
                required
              >
                <option value="">— Pick staff —</option>
                {allStaff.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.full_name} ({m.role_label})
                  </option>
                ))}
              </select>
              <select
                value={row.shift}
                onChange={(e) => update(idx, "shift", e.target.value)}
              >
                {SHIFTS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="vsop-icon-btn"
                onClick={() => removeRow(idx)}
                aria-label="Remove row"
                title="Remove row"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="vsop-btn-ghost vsop-roster-add"
            onClick={addRow}
          >
            <Plus size={14} /> Add another shift
          </button>
        </div>

        {error && <div className="vsop-modal-error">{error}</div>}

        <div className="vsop-modal-actions">
          <button
            type="button"
            className="vsop-btn-secondary"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="vsop-btn-primary"
            disabled={busy}
          >
            {busy ? "Saving…" : "Save roster"}
          </button>
        </div>
      </form>
    </div>
  );
}
